import { EFinancingProgram, EPaymentFrequency } from '@if/core/types'
import { useNumberOptionEnum, useTranslatedOptionEnum } from '@if/mui'
import { Constants, CreditApplication } from '@src/data/types'
import { MechanicalWarrantyProduct } from '@src/data/types/MechanicalWarrantyProduct'
import { useEffect, useMemo } from 'react'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'
import { MechanicalWarrantyComputeResultDto } from '../../types/ComputeMechanicalWarrantyResultDto'
import { MechanicalWarrantyWorksheetDto } from '../../types/mechanicalWarrantyWorksheetSchema'

const config = Constants.financingProgramConfigs[EFinancingProgram.MechanicalWarranty]

interface UseMechanicalWarrantyWorksheetDetailsParams {
  control: Control<MechanicalWarrantyWorksheetDto>
  setValue: UseFormSetValue<MechanicalWarrantyWorksheetDto>
  mechanicalWarrantyProducts: MechanicalWarrantyProduct[]
  computedLoanAmounts: MechanicalWarrantyComputeResultDto | null
  creditApplicationExpiresOn: Date
  creditApplication?: CreditApplication
}

export const useMechanicalWarrantyWorksheetDetails = ({
  control,
  setValue,
  mechanicalWarrantyProducts,
  computedLoanAmounts,
  creditApplicationExpiresOn,
  creditApplication,
}: UseMechanicalWarrantyWorksheetDetailsParams) => {
  // Form field values
  const [paymentFrequency, mechanicalWarrantyProductId] = useWatch({
    control,
    name: ['paymentFrequency', 'mechanicalWarrantyProductId'],
  })

  // Computed values
  const selectedProduct = mechanicalWarrantyProducts.find((product) => product.id === mechanicalWarrantyProductId)
  const maxActivationDate = creditApplicationExpiresOn

  // Options for dropdowns
  const allowedPaymentFrequencies = useMemo(() => {
    const supportedFrequencies = config.supportedPaymentFrequencies

    if (creditApplication?.isSinglePayment) {
      return supportedFrequencies.filter((freq) => freq === EPaymentFrequency.Single)
    } else {
      return supportedFrequencies.filter(
        (freq) => freq === EPaymentFrequency.Monthly || freq === EPaymentFrequency.BiWeekly,
      )
    }
  }, [creditApplication?.isSinglePayment])

  const paymentFrequencyOptions = useTranslatedOptionEnum('EPaymentFrequency', allowedPaymentFrequencies, 'enum')

  const termOptions = useNumberOptionEnum(selectedProduct?.duration ? [selectedProduct.duration] : [])

  // Auto-select first product if no selection exists
  useEffect(() => {
    const hasNoSelection = !mechanicalWarrantyProductId || mechanicalWarrantyProductId === ''
    const hasProducts = mechanicalWarrantyProducts.length > 0

    if (hasProducts && hasNoSelection) {
      const firstProduct = mechanicalWarrantyProducts[0]
      setValue('mechanicalWarrantyProductId', firstProduct.id, { shouldDirty: true, shouldValidate: true })
    }
  }, [mechanicalWarrantyProducts, mechanicalWarrantyProductId, setValue])

  useEffect(() => {
    if (computedLoanAmounts?.amountRequested != null) {
      setValue('amountRequested', computedLoanAmounts.amountRequested, { shouldDirty: true, shouldValidate: true })
    }
  }, [computedLoanAmounts?.amountRequested, setValue])

  useEffect(() => {
    if (selectedProduct?.duration) {
      setValue('term', Number(selectedProduct.duration), { shouldDirty: true, shouldValidate: true })
    }
  }, [selectedProduct?.duration, setValue])

  return {
    selectedProduct,
    maxActivationDate,
    paymentFrequency,
    paymentFrequencyOptions,
    termOptions,
  } as const
}
